<template>
  <div>
    <div v-if="isActiveCurrentStopwatch && !config.scorekeeper_stopwatch_disabled" class="text-center mb-2 px-3">
      <h5
        class="mb-0 font-weight-bold py-2 px-3 rounded text-white d-inline-block cursor-pointer"
        role="button"
        :class="{
                'bg-success': isRunning,
                'bg-danger pulse-timer': !isRunning
              }"
        @click="toggleTime"
      >
        {{ currentTimeToShow }}
      </h5>
    </div>
    <div class="text-center mb-2">
      <div class="font-weight-bold small" style="font-size: 1.4rem;">{{ score }}</div>
      <div v-if="roundScore" class="text-muted">{{ roundScore }}</div>
      <small v-if="scoreByPeriod" class="text-muted">{{ scoreByPeriod }}</small>
    </div>
    <div class="live-wrapper teams-wrapper mb-2" :class="{'flex-row-reverse': reversedTeams}">
      <div class="half">
        <a @click="selectEntity('team', 'home', model.homeTeam.uuid)"
           class="btn action-button team unselectable"
           style="width: 100%;"
           :style="{ color: '#282828', background: homeTeamColor }"
           :class="{'btn-success': relatedTo === 'team' && teamOn === 'home'}">
          {{ model.homeTeam.name }}
        </a>
      </div>
      <div class="half">
        <a @click="selectEntity('team', 'away', model.awayTeam.uuid)"
           class="btn action-button team unselectable"
           :style="{ color: '#282828', background: awayTeamColor }"
           :class="{'btn-success': relatedTo === 'team' && teamOn === 'away'}"
           style="width: 100%;">{{ model.awayTeam.name }}
        </a>
      </div>
    </div>
    <missing-position-warning :positions="missingPositions.home" :team="model.homeTeam.name" />
    <missing-position-warning :positions="missingPositions.away" :team="model.awayTeam.name" />
    <div v-if="model.hasSubstitutions" class="py-2 mb-2">
      <button class="btn btn-secondary btn-block" @click="openSubstitutionModal">
        {{ $t('game.substitution') }}
      </button>
    </div>
    <div class="live-wrapper" :class="{'flex-row-reverse': reversedTeams}">
      <div class="half">
        <template v-for="(player, index) in homePlayersSorted">
          <div v-if="index === homePlayingCount" :key="'home-bench-' + player.uuid" class="status-divider text-muted small text-center w-100 mb-1 text-uppercase">--- {{ $t('game.bench') }} ---</div>
          <a
            :key="player.uuid"
            class="btn action-button player mb-2 text-left unselectable"
            style="width: 100%;"
            :class="{'bg-success': log.on === player.uuid,
                      'bg-info': log.teammate === player.uuid,
                      'disabled': disableHome,
                      'bg-warning': log.against === player.uuid}"
            @click="selectEntity('player', 'home', player.uuid)"
            :style="{color: playerPositionColor(player)}"
          >
            <span class="number">{{ player.number_of_jersey }}</span>
            <span class="name">{{ player.name }}</span>
            <template v-if="player.player_tags && player.player_tags.length">
              <span v-for="tag in player.player_tags" :key="tag.uuid" class="player-tag-badge ml-1" :style="playerTagStyle(tag)">{{ tag.name }}</span>
            </template>
          </a>
        </template>
      </div>
      <div class="half">
        <template v-for="(player, index) in awayPlayersSorted">
          <div v-if="index === awayPlayingCount" :key="'away-bench-' + player.uuid" class="status-divider text-muted small text-center w-100 mb-1 text-uppercase">--- {{ $t('game.bench') }} ---</div>
          <a
            :key="player.uuid"
            class="btn action-button player mb-2 text-left unselectable"
            :class="{'bg-success': log.on === player.uuid,
                      'bg-info': log.teammate === player.uuid,
                      'disabled': disableAway,
                      'bg-warning': log.against === player.uuid}"
            style="width: 100%;"
            @click="selectEntity('player', 'away', player.uuid)"
            :style="{color: playerPositionColor(player)}"
          >
            <span class="number">{{ player.number_of_jersey }}</span>
            <span class="name">{{ player.name }}</span>
            <template v-if="player.player_tags && player.player_tags.length">
              <span v-for="tag in player.player_tags" :key="tag.uuid" class="player-tag-badge ml-1" :style="playerTagStyle(tag)">{{ tag.name }}</span>
            </template>
          </a>
        </template>
      </div>
    </div>
    <div
      class="popup-wrapper"
      :class="{'show': showMobileTeamActionsPopup || showMobilePlayerActionsPopup || mobileSettingsValue}"
    >
      <div
        class="popup"
      >
        <div v-if="showMobileTeamActionsPopup" class="popup-inner">
          <h4 v-if="config.live.by_period && relatedTo === 'team' && teamOn === 'home'" class="text-center mb-3">
            {{ model.homeTeam.name }}</h4>
          <h4 v-if="config.live.by_period && relatedTo === 'team' && teamOn === 'away'" class="text-center mb-3">
            {{ model.awayTeam.name }}</h4>
          <div class="popup-body">
            <div
              v-if="config.live.by_period && relatedTo === 'team' && teamOn === 'home'"
              class="team-period-stats-wrapper mb-4"
            >
              <div class="d-flex align-items-center stat-wrapper">
                <span class="w-100 stat-title">{{ $t('period') }}</span>
                <span
                  v-for="(gamePeriod, p) in model.gamePeriods"
                  :key="'homeTeamByPeriod-' + p"
                  class="stat"
                >
                  {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT' + gamePeriod.periodNumber }}
                </span>
              </div>
              <div
                v-for="tableBody in config.live.by_period.team_table" :key="'homeTeamByPeriod-' + tableBody"
                class="d-flex align-items-center stat-wrapper"
              >
                <span class="w-100 stat-title">{{ $t(`live.${tableBody}`) }}</span>
                <span
                  v-for="(period, p) in model.homeTeam.by_period_stats"
                  :key="'homeTeamByPeriod-' + p"
                  class="stat"
                >
                  {{ period ? period[tableBody] : '-' }}
                </span>
              </div>
            </div>
            <div
              v-if="config.live.by_period && relatedTo === 'team' && teamOn === 'away'"
              class="team-period-stats-wrapper mb-4"
            >
              <div class="d-flex align-items-center stat-wrapper">
                <span class="w-100 stat-title">{{ $t('period') }}</span>
                <span
                  v-for="(gamePeriod, p) in model.gamePeriods"
                  :key="'awayTeamByPeriod-' + p"
                  class="stat"
                >
                  {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT' + gamePeriod.periodNumber }}
                </span>
              </div>
              <div
                v-for="tableBody in config.live.by_period.team_table" :key="'awayTeamByPeriod-' + tableBody"
                class="d-flex align-items-center stat-wrapper"
              >
                <span class="w-100 stat-title">{{ $t(`live.${tableBody}`) }}</span>
                <span
                  v-for="(period, p) in model.awayTeam.by_period_stats"
                  :key="'awayTeamByPeriod-' + p"
                  class="stat"
                >
                  {{ period ? period[tableBody] : '-' }}
                </span>
              </div>
            </div>
            <div class="live-wrapper">
              <div v-for="(button, n) in buttonTeamGroups" :key="'buttonTeamGroups-' + n" class="half"
                   style="padding: 5px;">
                <a class="btn action-button position-relative w-100 unselectable" :disabled="log.teamOn === '' || relatedTo !== 'team'"
                   :class="{
                    'disabled': log.teamOn === '' || relatedTo !== 'team',
                    'active': clickedButton === n,
                    'has-label': !isMobile && button.hotKey?.label
                    }"
                   @click="selectType(n)"
                   :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
                  <span>{{ $t('live.sports.' + config.sport + '.game_log_buttons.team.' + n) }}</span>
                  <span v-if="!isMobile && button.hotKey?.label" class="hot-key-label">{{ button.hotKey?.label }}</span>
                </a>
              </div>
            </div>
          </div>
          <div class="popup-close d-flex justify-content-end mb-2">
            <button @click="selectEntity('team', '', null)" class="btn btn-sm btn-danger w-100">{{
                $t('cancel')
              }}
            </button>
          </div>
        </div>
        <div v-if="showMobilePlayerActionsPopup && selectedPlayer" class="popup-inner" id="playerPopup">
          <h4 class="text-center m-0 pt-2" id="playerPopupTitle">
            {{ `${selectedPlayer.number_of_jersey}. ${selectedPlayer.name}` }}</h4>
          <div class="popup-body" id="playerPopupBody" :style="{ 'max-height': playerPopupBodyMaxHeight + 'px'}">
            <div class="bg-light p-1" id="playerStats">
              <div class="d-flex align-items-center justify-content-center player-stat-wrapper">
                <div
                  v-for="(stat, key) in config.live.player_table"
                  :key="stat"
                  class="player-stat"
                >
                  <div class="d-flex flex-column align-items-center player-stat-inner">
                    <span>{{ key }}</span>
                    <p>{{ selectedPlayer[stat] }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="player-actions-wrapper"
              id="playerActions"
              :style="{ 'max-height': playerActionsScroll + 'px'}"
            >
              <div v-for="(buttonGroup, n) in buttonPlayerGroups" :key="'buttonPlayerGroup-' + n" class="live-wrapper">
                <div
                  v-for="(button, buttonType) in buttonGroup"
                  :key="'buttonPlayerTypes-' + buttonType"
                  :class="[button.class || 'half']"
                >
                  <a class="btn action-button position-relative w-100 unselectable" :disabled="log.on === '' || relatedTo !== 'player'"
                     :class="{
                      'disabled': log.teamOn === '' || relatedTo !== 'player' || (type !== buttonType && type !== ''),
                      'active': clickedButton === buttonType,
                      'has-label': !isMobile && button.hotKey?.label
                      }"
                     @click="selectType(buttonType)"
                     :style="{backgroundColor: button.bgColor ? button.bgColor: '#70b9eb'}">
                    <span>{{
                        $t('live.sports.' + config.sport + '.game_log_buttons.player.' + buttonType)
                      }}</span>
                    <span v-if="!isMobile && button.hotKey?.label"
                          class="hot-key-label">{{ button.hotKey?.label }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="popup-close d-flex justify-content-end mb-2" id="playerPopupClose">
            <button @click="selectEntity('player', '', null)" class="btn btn-sm btn-danger w-100">{{
                $t('cancel')
              }}
            </button>
          </div>
        </div>
        <div v-if="mobileSettingsValue" class="popup-inner">
          <h4 class="text-center mb-3">{{ $t('game.settings') }}</h4>
          <div class="popup-body">
            <div />
            <div>
              <nav aria-label="Page navigation" class="mb-3">
                <div class="py-2 bg-light">
                  <h4 class="text-center mb-2">Period</h4>
                  <ul class="periods pagination justify-content-center mb-3">
                    <li v-for="gamePeriod in model.gamePeriods" :key="gamePeriod.periodType + '_' + gamePeriod.periodNumber" class="page-item"
                        :class="{active: currentPeriod && currentPeriod.uuid === gamePeriod.uuid}">
                      <a class="page-link mr-2" @click="setPeriod(gamePeriod.periodType, gamePeriod.periodNumber)">
                        {{ gamePeriod.periodType === 'REGULAR' ? gamePeriod.periodNumber : 'OT' + gamePeriod.periodNumber }}
                      </a>
                    </li>
                    <li class="page-item" v-if="model.gamePeriods && model.gamePeriods.filter(p => p.periodType === 'REGULAR').length < model.schedule.numberOfRegularPeriods">
                      <a class="page-link" @click="setPeriod('REGULAR', model.gamePeriods.filter(p => p.periodType === 'REGULAR').length + 1)">+</a>
                    </li>
                    <li class="page-item" v-else-if="model.gamePeriods">
                      <a class="page-link" @click="setPeriod('OVERTIME', model.gamePeriods.filter(p => p.periodType === 'OVERTIME').length + 1)">+ OT</a>
                    </li>
                  </ul>
                </div>
              </nav>
              <div class="custom-control custom-switch py-1 px-2 rounded d-flex justify-content-center mb-4">
                <input type="checkbox" class="custom-control-input" :checked="reverseTeamsValue" id="customSwitch1"
                      @change="reverseTeams">
                <label class="custom-control-label" for="customSwitch1" role="button">{{
                    $t('live.switchTeams')
                  }}</label>
              </div>
              <div class="custom-control custom-switch py-1 px-2 rounded d-flex justify-content-center mb-4">
                <input type="checkbox" class="custom-control-input" :checked="autoUnselectValue" id="mobileAutoUnselect"
                      @change="autoUnselect">
                <label class="custom-control-label" for="mobileAutoUnselect" role="button">{{
                    $t('live.autoUnselect')
                  }}</label>
              </div>
              <button class="btn btn-warning w-100" @click="endGame()">{{ $t('game.endGame') }}</button>
            </div>
            <div />
          </div>
          <div class="popup-close d-flex justify-content-end mb-2">
            <button @click="showMobileSettings()" class="btn btn-sm btn-danger w-100">{{ $t('close') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showSubstitutionModal" class="substitution-modal-overlay" @keydown="handleSubstitutionKeyDown" @click.self="closeSubstitutionModal" tabindex="0">
      <div class="substitution-modal">
        <div class="modal-header d-flex flex-column p-2">
          <div class="d-flex justify-content-between align-items-center w-100 mb-2">
            <h4 class="mb-0 text-truncate mr-2" style="max-width: 50%;">{{ !model.awayTeam.players.filter(p => p.isInGame).length && !model.awayTeam.players.filter(p => p.isInGame).length ? $t('game.choose_starter_players') : $t('game.substitution') }}</h4>

            <div class="d-flex align-items-center">
              <button type="button"
                      class="btn btn-sm btn-dual bg-info"
                      @click="toggleSidebar('substitutions')">
                <i class="fa fa-history text-white"></i>
              </button>
            </div>
          </div>
          <div class="btn-group btn-group-sm w-100">
            <button class="btn"
                    :class="substitutionMode === 'classic' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="substitutionMode !== 'classic' && toggleSubstitutionMode()">
              {{ $t('game.classic_mode') || 'Classic' }}
            </button>
            <button class="btn"
                    :class="substitutionMode === 'on-court' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="substitutionMode !== 'on-court' && toggleSubstitutionMode()">
              {{ $t('game.on_court_mode') || 'On Court' }}
            </button>
          </div>
        </div>
        <div class="modal-body p-0">
          <div class="team-headers-fixed border-bottom bg-light px-2 py-1">
            <div class="row no-gutters">
              <div class="col-6 border-right px-1">
                <h6 class="text-truncate mb-0 d-flex justify-content-between align-items-center">
                  <span class="text-truncate mr-1">{{ substitutionLeftTeam.name }}</span>
                  <span class="badge extra-small" :class="{
                    'badge-danger': substitutionDiffLeft.next > maxTeamPlayersInGame,
                    'badge-success': substitutionDiffLeft.next === maxTeamPlayersInGame,
                    'badge-warning': substitutionDiffLeft.next < maxTeamPlayersInGame
                  }">
                    {{ substitutionDiffLeft.next }}/{{ maxTeamPlayersInGame }}
                  </span>
                </h6>
              </div>
              <div class="col-6 px-1">
                <h6 class="text-truncate mb-0 d-flex justify-content-between align-items-center">
                  <span class="text-truncate mr-1">{{ substitutionRightTeam.name }}</span>
                  <span class="badge extra-small" :class="{
                    'badge-danger': substitutionDiffRight.next > maxTeamPlayersInGame,
                    'badge-success': substitutionDiffRight.next === maxTeamPlayersInGame,
                    'badge-warning': substitutionDiffRight.next < maxTeamPlayersInGame
                  }">
                    {{ substitutionDiffRight.next }}/{{ maxTeamPlayersInGame }}
                  </span>
                </h6>
              </div>
            </div>
          </div>
          <div class="modal-scroll-content p-2">
            <div v-if="substitutionMode === 'on-court'" class="row no-gutters">
              <div class="col-6 border-right px-1">
                <div class="status-header text-primary small font-weight-bold border-bottom mb-1">
                  {{ $t('game.select_players_on_court') || 'On Court' }}
                </div>
                <div class="text-right mb-1">
                  <button class="btn btn-xs border-0 py-0"
                          :class="isTeamAnySelected(leftTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                          style="font-size: 0.65rem;"
                          @click="toggleOnCourtSelection(leftTeamKey)">
                    {{ isTeamAnySelected(leftTeamKey) ? ($t('game.unselect_all_players') || 'Un') : ($t('game.select_current_lineup') || 'Sel') }}
                  </button>
                </div>
                <div class="player-list">
                  <div v-for="player in leftPlayersForSubstitution" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-primary border-primary': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(leftTeamKey, player.uuid)" @toggle="toggleDraftPosition(leftTeamKey, player.uuid, $event)" />
                  </div>
                </div>
              </div>
              <div class="col-6 px-1">
                <div class="status-header text-primary small font-weight-bold border-bottom mb-1">
                  {{ $t('game.select_players_on_court') || 'On Court' }}
                </div>
                <div class="text-right mb-1">
                  <button class="btn btn-xs border-0 py-0"
                          :class="isTeamAnySelected(rightTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                          style="font-size: 0.65rem;"
                          @click="toggleOnCourtSelection(rightTeamKey)">
                    {{ isTeamAnySelected(rightTeamKey) ? ($t('game.unselect_all_players') || 'Un') : ($t('game.select_current_lineup') || 'Sel') }}
                  </button>
                </div>
                <div class="player-list">
                  <div v-for="player in rightPlayersForSubstitution" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-primary border-primary': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(rightTeamKey, player.uuid)" @toggle="toggleDraftPosition(rightTeamKey, player.uuid, $event)" />
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="row no-gutters">
              <div class="col-6 border-right px-1">
                <div class="status-header text-success small font-weight-bold border-bottom mb-1">
                  {{ $t('game.in_game') }}
                </div>
                <div class="player-list mb-1">
                  <div v-for="player in leftPlayersForSubstitution.filter(p => p.isInGame)" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(leftTeamKey, player.uuid)" @toggle="toggleDraftPosition(leftTeamKey, player.uuid, $event)" />
                  </div>
                  <div v-if="!substitutionLeftTeam.players.filter(p => p.isInGame).length" class="text-muted extra-small">{{ $t('game.no_players_in_game') }}</div>
                  <div v-else class="text-right mb-1">
                    <button class="btn btn-xs border-0 py-0"
                            :class="isTeamAllInGameSelected(leftTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                            style="font-size: 0.65rem;"
                            @click="selectAllInGame(leftTeamKey)">{{ $t('game.select_all_players') }}</button>
                  </div>
                </div>

                <div class="status-header text-muted small font-weight-bold border-bottom mb-1">{{ substitutionLeftTeam.players.filter(p => p.isInGame).length ? $t('game.bench') : $t('game.choose_starter_players') }}</div>
                <div class="player-list">
                  <div v-for="player in leftPlayersForSubstitution.filter(p => !p.isInGame)" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === leftTeamKey && leftPlayersForSubstitution[focusedPlayerIndex] && leftPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(leftTeamKey, player.uuid)" @toggle="toggleDraftPosition(leftTeamKey, player.uuid, $event)" />
                  </div>
                </div>
              </div>

              <div class="col-6 px-1">
                <div class="status-header text-success small font-weight-bold border-bottom mb-1">
                  {{ $t('game.in_game') }}
                </div>
                <div class="player-list mb-1">
                  <div v-for="player in rightPlayersForSubstitution.filter(p => p.isInGame)" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'bg-light-success': !selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(rightTeamKey, player.uuid)" @toggle="toggleDraftPosition(rightTeamKey, player.uuid, $event)" />
                  </div>
                  <div v-if="!substitutionRightTeam.players.filter(p => p.isInGame).length" class="text-muted extra-small">{{ $t('game.no_players_in_game') }}</div>
                  <div v-else class="text-right mb-1">
                    <button class="btn btn-xs border-0 py-0"
                            :class="isTeamAllInGameSelected(rightTeamKey) ? 'btn-primary' : 'btn-outline-primary'"
                            style="font-size: 0.65rem;"
                            @click="selectAllInGame(rightTeamKey)">{{ $t('game.select_all_players') }}</button>
                  </div>
                </div>

                <div class="status-header text-muted small font-weight-bold border-bottom mb-1">{{ substitutionRightTeam.players.filter(p => p.isInGame).length ? $t('game.bench') : $t('game.choose_starter_players') }}</div>
                <div class="player-list">
                  <div v-for="player in rightPlayersForSubstitution.filter(p => !p.isInGame)" :key="player.uuid"
                       class="player-item d-flex align-items-center flex-wrap mb-1"
                       :class="{
                         'bg-light-info': selectedSubstitutions.includes(player.uuid),
                         'focused-player': focusedPlayerIndex !== -1 && focusedTeam === rightTeamKey && rightPlayersForSubstitution[focusedPlayerIndex] && rightPlayersForSubstitution[focusedPlayerIndex].uuid === player.uuid
                       }"
                       @click="togglePlayerSelection(player)">
                    <input type="checkbox" :checked="selectedSubstitutions.includes(player.uuid)" class="mr-1">
                    <template v-if="previousSubstitutions.length">
                      <span v-if="previousSubstitutions.includes(player.uuid)" class="badge badge-info mr-1" style="font-size: 0.6rem; padding: 0.1rem 0.2rem;">P</span>
                      <span v-else class="badge border-0 mr-1 text-transparent bg-transparent" style="font-size: 0.6rem; padding: 0.1rem 0.2rem; color: transparent;">P</span>
                    </template>
                    <small class="jersey-number mr-1">{{ player.number_of_jersey }}</small>
                    <small class="player-name text-truncate">{{ player.name }}</small>
                    <template v-if="player.player_tags && player.player_tags.length">
                      <span v-for="tag in player.player_tags" :key="tag.uuid" class="badge ml-1" :style="playerTagStyle(tag)" style="font-size: 0.6em;">{{ tag.name }}</span>
                    </template>
                    <player-position-buttons v-if="isPlayerInDraftLineup(player)" :positions="lineupPositions" :active="getDraftPosition(rightTeamKey, player.uuid)" @toggle="toggleDraftPosition(rightTeamKey, player.uuid, $event)" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer d-flex flex-column p-2">
          <div v-if="draftMissingPositions.home.length || draftMissingPositions.away.length" class="w-100">
            <missing-position-warning :positions="draftMissingPositions[leftTeamKey]" :team="model[leftTeamKey + 'Team'].name" />
            <missing-position-warning :positions="draftMissingPositions[rightTeamKey]" :team="model[rightTeamKey + 'Team'].name" />
          </div>
          <div v-if="!config.scorekeeper_stopwatch_disabled" class="d-flex align-items-center w-100 mb-2">
            <label class="mb-0 mr-2 small">{{ $t('game.substitution_time') }}:</label>
            <div class="d-flex align-items-center">
              <input
                ref="subInput0"
                v-model="substitutionTimeInputs[0]"
                :placeholder="placeholderSubstitutionTime[0]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub-mobile"
                :class="{'has-error': substitutionInputErrors[0]}"
                @input="onSubstitutionTimeInput(0, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(0, $event, $refs)"
              >
              <input
                ref="subInput1"
                v-model="substitutionTimeInputs[1]"
                :placeholder="placeholderSubstitutionTime[1]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub-mobile"
                :class="{'has-error': substitutionInputErrors[1]}"
                @input="onSubstitutionTimeInput(1, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(1, $event, $refs)"
              >
              <span class="h6 mb-0 mx-1">:</span>
              <input
                ref="subInput2"
                v-model="substitutionTimeInputs[2]"
                :placeholder="placeholderSubstitutionTime[2]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub-mobile"
                :class="{'has-error': substitutionInputErrors[2]}"
                @input="onSubstitutionTimeInput(2, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(2, $event, $refs)"
              >
              <input
                ref="subInput3"
                v-model="substitutionTimeInputs[3]"
                :placeholder="placeholderSubstitutionTime[3]"
                type="text"
                pattern="\d*"
                inputmode="numeric"
                maxlength="1"
                class="text-center time-input-sub-mobile"
                :class="{'has-error': substitutionInputErrors[3]}"
                @input="onSubstitutionTimeInput(3, $event, $refs)"
                @keydown="onSubstitutionTimeKeydown(3, $event, $refs)"
              >
            </div>
          </div>

          <div class="d-flex align-items-center w-100 justify-content-end">
            <div v-if="substitutionLoading" class="mr-2">
              <i class="fa fa-spinner fa-spin mr-1 small"></i> <small>{{ $t('loading') }}</small>
            </div>
            <button class="btn btn-sm btn-secondary mr-2" :disabled="substitutionLoading" @click="closeSubstitutionModal">{{ $t('cancel') }}</button>
            <button class="btn btn-sm btn-primary" :disabled="!isSubstitutionValid || substitutionLoading" @click="confirmSubstitution">{{ $t('confirm') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showOptionsModal" class="options-modal-overlay" @click.self="closeOptionsModal">
      <div class="options-modal">
        <div class="options-modal-header d-flex align-items-center justify-content-between p-3">
          <button v-if="optionsModalStack.length > 1" class="btn btn-sm btn-outline-secondary mr-2" @click="optionsModalBack">
            <i class="fa fa-arrow-left"></i>
          </button>
          <span class="font-w600">{{ $t('selectOption') || 'Select option' }}</span>
          <button class="btn btn-sm btn-outline-secondary" @click="closeOptionsModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="options-modal-body p-3">
          <div class="row">
            <div
              v-for="(option, optionKey, idx) in optionsModalStack[optionsModalStack.length - 1].options"
              :key="optionKey"
              class="col-6 mb-3"
            >
              <a
                class="btn action-button w-100 text-white position-relative"
                :style="{ backgroundColor: option.bgColor || '#70b9eb' }"
                @click="selectOption(optionKey, option)"
              >
                <span class="hot-key-label">{{ idx + 1 }}</span>
                {{ option.label || optionKey }}
                <i v-if="option.options" class="fa fa-chevron-right ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useLive } from '@/views/live/use/useLive'
import { useStopwatch } from '@/use/useStopwatch'
import MissingPositionWarning from '@/components/le/MissingPositionWarning'
import PlayerPositionButtons from '@/components/le/PlayerPositionButtons'

export default {
  components: { MissingPositionWarning, PlayerPositionButtons },
  setup () {
    const live = useLive()
    const { isRunning, isActiveCurrentStopwatch, currentTimeToShow, toggleTime } = useStopwatch()
    return { ...live, isRunning, isActiveCurrentStopwatch, currentTimeToShow, toggleTime }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.showMobilePlayerActionsPopup) {
        const element = document.getElementById('playerActions')
        this.playerActionsHeight = element.clientHeight
      }
    })
  },
  data () {
    return {
      homeTeamColor: localStorage.getItem('homeTeamColor') ?? '#70b9eb',
      awayTeamColor: localStorage.getItem('awayTeamColor') ?? '#70b9eb'
    }
  },
  watch: {
    // The rosters can be reloaded while the popup is open: close it when its player is gone
    selectedPlayer (player) {
      if (!player && this.showMobilePlayerActionsPopup) {
        this.showMobilePlayerActionsPopup = false
      }
    }
  },
  computed: {
    mobileSettingsValue () {
      return this.$store.state.main.mobileSettings
    },
    reverseTeamsValue () {
      return this.$store.state.main.reversedTeams
    },
    autoUnselectValue () {
      return this.$store.state.main.autoUnselect
    },
    substitutionLeftTeam () {
      return this.reversedTeams ? this.model.awayTeam : this.model.homeTeam
    },
    substitutionRightTeam () {
      return this.reversedTeams ? this.model.homeTeam : this.model.awayTeam
    },
    leftPlayersForSubstitution () {
      return this.reversedTeams ? this.awayPlayersForSubstitution : this.homePlayersForSubstitution
    },
    rightPlayersForSubstitution () {
      return this.reversedTeams ? this.homePlayersForSubstitution : this.awayPlayersForSubstitution
    },
    substitutionDiffLeft () {
      return this.reversedTeams ? this.substitutionDiff.away : this.substitutionDiff.home
    },
    substitutionDiffRight () {
      return this.reversedTeams ? this.substitutionDiff.home : this.substitutionDiff.away
    },
    leftTeamKey () {
      return this.reversedTeams ? 'away' : 'home'
    },
    rightTeamKey () {
      return this.reversedTeams ? 'home' : 'away'
    },
    selectedPlayer () {
      const homePlayers = this.model.homeTeam.players
      const awayPlayers = this.model.awayTeam.players
      const allPlayers = homePlayers.concat(awayPlayers)
      return allPlayers.find(player => player.uuid === this.log.on)
    }
  },
  methods: {
    // Whether the player will be on the court once the substitution is confirmed (only those get a position)
    isPlayerInDraftLineup (player) {
      const selected = this.selectedSubstitutions.includes(player.uuid)
      if (this.substitutionMode === 'on-court') return selected
      return selected ? !player.isInGame : player.isInGame
    },
    playerTagStyle (tag) {
      if (!tag || !tag.color) return { backgroundColor: '#6c757d', color: '#fff', borderRadius: '3px', padding: '1px 4px', fontSize: '0.7em' }
      return { backgroundColor: tag.color, color: '#fff', borderRadius: '3px', padding: '1px 4px', fontSize: '0.7em' }
    },
    rerender () {
      this.$forceUpdate()
    },
    onPlayerContextMenuClick (value) {
      this.playerContextMenuClicked(value)
      this.$forceUpdate()
    },
    onTeamContextMenuClick (value) {
      this.teamContextMenuClicked(value)
      this.$forceUpdate()
    },
    reverseTeams () {
      this.$store.commit('main/reversedTeams', this.reverseTeamsValue)
    },
    autoUnselect () {
      this.$store.commit('main/autoUnselect', !this.autoUnselectValue)
    },
    showMobileSettings () {
      this.$store.commit('main/mobileSettings', this.mobileSettingsValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.live-wrapper {
  display: flex;
  align-items: stretch;
  margin: 0 -4px;
  flex-wrap: wrap;
}

.half {
  max-width: 50%;
  flex: 0 0 50%;
  padding: 4px;
}

.full {
  max-width: 100%;
  flex: 0 0 100%;
  padding: 4px;
}

.teams-wrapper {
  position: sticky;
  top: 68px;
  background: #f5f5f5;
  @media (max-width: 1250px) {
    top: 40px;
  }
}

.btn {
  font-size: 12px;

  &.player {
    background-color: #dfdfdf;
    justify-content: flex-start;
    &.action-button {
      color: #282828;

      &:active {
        background: #46c37b !important
      }
    }
  }

  &.action-button,
  &.action-button:hover {
    color: #fff;
  }
}

.pulse-timer {
  animation: pulse-timer 1.2s infinite;
}

@keyframes pulse-timer {
  0% {
    transform: scale(0.95);
  }

  70% {
    transform: scale(1);
  }

  100% {
    transform: scale(0.95);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.player {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;

  .number {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: #000;
    background: #fff;
    height: 20px;
    min-width: 24px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .name {
    font-size: 10px;
  }
}

.popup {
  width: calc(100% - 16px);
  height: calc(100% - 48px);
  background-color: #fff;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: stretch;

  &-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    height: 100dvh;
    width: 100vw;
    background-color: rgba(#000, 0.2);
    z-index: 999998;
    display: none;
    align-items: flex-start;
    justify-content: center;
    padding-top: 8px;

    &.show {
      display: flex;
    }
  }

  &-inner {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    gap: 8px;
  }

  &-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;

    .col-sm-12 {
      padding: 4px;
    }
  }
}

.timer-toggle {
  bottom: 8px;
  right: 8px;
  z-index: 999999;

  &.time-editing {
    bottom: 48px;
  }
}

.page-item {

  .page-link {
    background-color: #f2f2f2;
  }

  &.active {
    .page-link {
      background-color: #e6e6e6;
    }
  }
}

.player-stat {
  padding: 2px;
  flex: 0 0 16.66666%;
  max-width: 16.66666%;

  &-wrapper {
    margin: -2px;
    flex-wrap: wrap;
  }

  &-inner {
    background-color: #f2f2f2;
    padding: 4px 2px;
  }

  span {
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    margin-bottom: 2px;
  }

  p {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
  }
}

.team-period-stats-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  font: {
    size: 14px;
    weight: 600;
  }
  line-height: 1;

  .stat {
    background-color: #e6e6e6;
    height: 32px;
    width: 32px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &-wrapper {
      gap: 4px;
    }

    &-title {
      background-color: #e6e6e6;
      height: 32px;
      display: flex;
      align-items: center;
      padding: 0 8px;
      border-radius: 4px;
    }
  }
}

.player-actions-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
}

.custom-switch .custom-control-label {
  border-radius: 4px;
  padding: 0.25rem 0.5rem 0.25rem 3rem;
  background-color: #f2f2f2;

  &::before {
    top: 6px;
    left: 6px;
  }

  &::after {
    top: 9px;
    left: 10px;
  }
}

.bg-light-success {
  background-color: #d4edda !important;
}

.extra-small {
  font-size: 8px;
}

.bg-light-success {
  background-color: #d4edda !important;
}

.bg-light-info {
  background-color: #d1ecf1 !important;
}

.player-item.focused-player {
  background-color: #e0e0e0 !important;
  box-shadow: inset 0 0 0 1px #3498db;
}
.substitution-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000000;
}

.substitution-modal {
  background: white;
  width: 95%;
  max-height: 90%;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.modal-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.team-headers-fixed {
  flex-shrink: 0;
}

.modal-scroll-content {
  flex-grow: 1;
  overflow-y: auto;
}

.extra-small {
  font-size: 0.7rem;
}

.player-list {
  margin-bottom: 20px;
}

.player-item {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #eee;
}

.jersey-number {
  font-weight: bold;
  width: 20px;
  display: inline-block;
}

.time-input-sub-mobile {
  outline: none;
  margin: 2px;
  padding: 4px;
  width: 30px;
  height: 30px;
  text-align: center;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  caret-color: transparent;
}

.time-input-sub-mobile:focus {
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.8);
  border-color: #007bff;
}

.time-input-sub-mobile.has-error {
  box-shadow: 0 0 6px rgba(220, 53, 69, 0.8);
  border: 1px solid rgba(220, 53, 69, 0.5);
}

.arrow-btn-sub-mobile {
  color: #888;
  font-size: 0.6rem;
  line-height: 1;
  transition: color 0.15s;
}

.arrow-btn-sub-mobile:hover {
  color: #007bff;
  text-decoration: none;
}

.arrow-btn-sub-mobile:focus {
  outline: none;
  box-shadow: none;
}

.time-input-sub-mobile::-webkit-outer-spin-button,
.time-input-sub-mobile::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.time-input-sub-mobile[type=number] {
  -moz-appearance: textfield;
}

.options-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000001;
}

.options-modal {
  background: #fff;
  border-radius: 8px;
  width: 320px;
  max-width: 95vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.options-modal-header {
  border-bottom: 1px solid #e4e7ed;
}

.options-modal-body {
  min-height: 80px;
}
</style>
